import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { getAppUrl } from '../../lib/url';
import { errorMessage } from '../../lib/errors';
import { MIN_PASSWORD_LENGTH, describePasswordRule } from '../../lib/password';
import { Loader2, Key, Mail, CheckCircle2, AlertCircle, Eye, EyeOff, ShieldCheck, Fingerprint, Trash2, Plus } from 'lucide-react';

interface Passkey {
    id: string;
    friendly_name?: string | null;
    created_at?: string | null;
}

// WebAuthn availability. A device without it (an old browser, or a locked-down
// managed device) should be told why the option is missing rather than handed a
// button that throws.
const PASSKEYS_SUPPORTED = typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined';

/**
 * True when Supabase Auth reports the project has passkeys switched off.
 *
 * GoTrue answers `404` with `error_code: "passkey_disabled"`. That is a
 * deployment fact, not a fault of the user's device or account, and it must not
 * be shown to them as a raw API error — hence matching on it explicitly rather
 * than lumping it in with genuine failures.
 */
const isPasskeyDisabled = (err: unknown): boolean => {
    const e = err as { code?: string; message?: string } | null;
    return e?.code === 'passkey_disabled'
        || /passkey.*disabled/i.test(e?.message ?? '');
};

export const SecuritySection = () => {
    const [loading, setLoading] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [sendingReset, setSendingReset] = useState(false);

    const [passkeys, setPasskeys] = useState<Passkey[]>([]);
    const [passkeysLoading, setPasskeysLoading] = useState(PASSKEYS_SUPPORTED);
    const [registeringPasskey, setRegisteringPasskey] = useState(false);
    // The project has passkeys switched off server-side. Distinct from "this
    // browser can't", and distinct from "you have none yet" — those three
    // states want three different things said to the user.
    const [passkeysUnavailable, setPasskeysUnavailable] = useState(false);

    // Used by the register/delete handlers. Safe to call setState directly here
    // — a handler is an event, not an effect.
    const loadPasskeys = useCallback(async () => {
        if (!PASSKEYS_SUPPORTED) return;
        try {
            const { data, error } = await supabase.auth.passkey.list();
            if (error) throw error;
            setPasskeys((data ?? []) as Passkey[]);
        } catch (err) {
            // Not fatal: the passkey API is in beta and can be disabled
            // project-side, in which case the rest of this screen must still
            // work. Logged rather than surfaced, since a user who has never
            // heard of passkeys should not be shown an error about them.
            console.warn('[SecuritySection] could not list passkeys:', err);
        }
    }, []);

    // The initial load is written as promise callbacks rather than
    // `useEffect(() => { load(); })`, because react-hooks/set-state-in-effect
    // (ratcheted in CI) rejects invoking a setState-containing function from an
    // effect body. Updating state from a callback once the external system
    // answers is precisely the pattern the rule does allow.
    useEffect(() => {
        if (!PASSKEYS_SUPPORTED) return;
        let cancelled = false;
        supabase.auth.passkey.list()
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) {
                    if (isPasskeyDisabled(error)) setPasskeysUnavailable(true);
                    return;
                }
                setPasskeys((data ?? []) as Passkey[]);
            })
            .catch((err) => {
                if (!cancelled && isPasskeyDisabled(err)) setPasskeysUnavailable(true);
                console.warn('[SecuritySection] could not list passkeys:', err);
            })
            .finally(() => { if (!cancelled) setPasskeysLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const handleRegisterPasskey = async () => {
        setMsg(null);
        setRegisteringPasskey(true);
        try {
            const { error } = await supabase.auth.registerPasskey();
            // A project with passkeys switched off answers
            // /auth/v1/passkeys/registration/options with a 404. That is a
            // configuration state, not a fault: say so in the panel and latch
            // passkeysUnavailable so the warning above stays accurate, rather
            // than showing a raw error. The button stays enabled on purpose —
            // see the note by the warning banner.
            if (error) {
                if (isPasskeyDisabled(error)) {
                    setPasskeysUnavailable(true);
                    setMsg({
                        type: 'error',
                        text: "Fingerprint sign-in isn't switched on for this workspace yet. Your password still works as normal.",
                    });
                    return;
                }
                throw error;
            }
            setMsg({ type: 'success', text: 'Passkey added. You can now sign in with it.' });
            await loadPasskeys();
        } catch (err) {
            // Dismissing the OS prompt is a normal choice, not an error.
            const name = (err as { name?: string })?.name;
            if (name !== 'NotAllowedError' && name !== 'AbortError') {
                setMsg({ type: 'error', text: errorMessage(err) || 'Could not add a passkey.' });
            }
        } finally {
            setRegisteringPasskey(false);
        }
    };

    const handleDeletePasskey = async (passkeyId: string) => {
        setMsg(null);
        try {
            const { error } = await supabase.auth.passkey.delete({ passkeyId });
            if (error) throw error;
            setMsg({ type: 'success', text: 'Passkey removed.' });
            await loadPasskeys();
        } catch (err) {
            setMsg({ type: 'error', text: errorMessage(err) || 'Could not remove that passkey.' });
        }
    };

    const handleUpdatePassword = async () => {
        setMsg(null);
        if (!currentPassword) {
            setMsg({ type: 'error', text: 'Enter your current password to confirm this change.' });
            return;
        }
        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setMsg({ type: 'error', text: describePasswordRule() });
            return;
        }
        if (newPassword !== confirmPassword) {
            setMsg({ type: 'error', text: 'Passwords do not match.' });
            return;
        }
        if (newPassword === currentPassword) {
            setMsg({ type: 'error', text: 'That is already your password. Choose a different one.' });
            return;
        }

        setLoading(true);
        try {
            // ------------------------------------------------------------------
            // Re-authenticate before changing the password.
            //
            // updateUser() alone only proves you hold a session, not that you
            // are the account owner. That made an unlocked laptop or a stolen
            // token enough to change the password and lock the real owner out of
            // their own books -- the opposite of what this screen is for.
            //
            // Supabase has no "verify current password" call, so the check is a
            // sign-in with the current credentials. It returns a fresh session
            // for the same user, which is harmless.
            // ------------------------------------------------------------------
            const { data: { user } } = await supabase.auth.getUser();
            if (!user?.email) throw new Error('Could not confirm who you are signed in as.');

            const { error: reauthError } = await supabase.auth.signInWithPassword({
                email: user.email,
                password: currentPassword,
            });
            if (reauthError) {
                setMsg({ type: 'error', text: 'That current password is not right.' });
                return;
            }

            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) throw error;

            setMsg({ type: 'success', text: 'Password updated. Keep it somewhere safe.' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setMsg({ type: 'error', text: errorMessage(err) || 'Could not update your password.' });
        } finally {
            setLoading(false);
        }
    };

    // For anyone who cannot complete the form above because they do not
    // remember the current password -- signed in on a device that kept the
    // session alive, which is common on a workshop tablet.
    const handleEmailMyself = async () => {
        setMsg(null);
        setSendingReset(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user?.email) throw new Error('No email address on this account.');
            const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
                redirectTo: `${getAppUrl()}/reset-password`,
            });
            if (error) throw error;
            setMsg({ type: 'success', text: `Reset link sent to ${user.email}. It can only be used once.` });
        } catch (err) {
            setMsg({ type: 'error', text: errorMessage(err) || 'Could not send the reset email.' });
        } finally {
            setSendingReset(false);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-fg">Security Settings</h3>
                <p className="text-sm text-fg-2">Manage your login credentials</p>
            </div>

            {msg && (
                <div className={`p-4 rounded-[var(--fds-radius)] flex items-center gap-3 ${msg.type === 'success' ? 'bg-success/10 text-success border border-success/20' : 'bg-danger/10 text-danger border border-danger/20'}`}>
                    {msg.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    <span className="text-sm font-medium">{msg.text}</span>
                </div>
            )}

            <div className="space-y-6 max-w-md">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-fg-2">Current Password</label>
                    <div className="relative">
                        <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-2 opacity-50" />
                        <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            autoComplete="current-password"
                            className="w-full bg-raised border border-line rounded-[var(--fds-radius)] pl-10 pr-10 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all font-mono"
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-2 hover:text-fg transition-colors"
                        >
                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    <p className="text-xs text-fg-2/70">
                        Confirms it's really you — not just someone at an unlocked screen.
                    </p>
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-fg-2">New Password</label>
                    <div className="relative">
                        <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-2 opacity-50" />
                        <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full bg-raised border border-line rounded-[var(--fds-radius)] pl-10 pr-10 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all font-mono"
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-2 hover:text-fg transition-colors"
                        >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-fg-2">Confirm Password</label>
                    <div className="relative">
                        <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-2 opacity-50" />
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full bg-raised border border-line rounded-[var(--fds-radius)] pl-10 pr-10 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all font-mono"
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-2 hover:text-fg transition-colors"
                        >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                <p className="text-xs text-fg-2/70">{describePasswordRule()}</p>

                <div className="pt-2 space-y-3">
                    <button
                        onClick={handleUpdatePassword}
                        disabled={loading || !newPassword || !currentPassword}
                        className="w-full flex items-center justify-center gap-2 bg-panel border border-line hover:bg-raised text-fg px-6 py-2.5 rounded-[var(--fds-radius)] font-medium transition-all disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
                    </button>

                    <div className="text-center">
                        <button
                            onClick={handleEmailMyself}
                            disabled={sendingReset}
                            className="text-xs text-fg-2 hover:text-accent underline underline-offset-2 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                        >
                            {sendingReset && <Loader2 className="w-3 h-3 animate-spin" />}
                            Don't remember your current password? Email me a reset link
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Passkeys ─────────────────────────────────────────────── */}
            <div className="border-t border-line pt-8 mt-8">
                <h4 className="font-bold text-fg flex items-center gap-2 mb-1">
                    <Fingerprint className="w-4 h-4 text-accent" /> Fingerprint &amp; Face ID
                </h4>
                <p className="text-sm text-fg-2 mb-4 max-w-lg">
                    Sign in with your fingerprint, face, or device PIN instead of typing a password.
                    Nothing to remember, nothing to phish — the key never leaves your device.
                    <span className="block mt-1 opacity-70">
                        Known technically as a <em>passkey</em>.
                    </span>
                </p>

                {/* Warn, but do NOT take the button away.
                    An earlier revision replaced the whole panel with this
                    notice, which removed the only way to try the thing and see
                    the real error — the exact thing someone debugging it needs.
                    Say what we know, then let them press it. */}
                {passkeysUnavailable && (
                    <div className="bg-gold-fill/10 border border-gold/20 rounded-[var(--fds-radius)] p-4 mb-4">
                        <p className="text-sm text-gold">
                            Fingerprint sign-in isn't switched on for this workspace yet, so adding
                            one will fail until it is. Nothing is wrong with your account, and your
                            password still works as normal. The button below is left enabled so you
                            can retry the moment it's turned on.
                        </p>
                    </div>
                )}

                {!PASSKEYS_SUPPORTED ? (
                    <div className="bg-raised border border-line rounded-[var(--fds-radius)] p-4">
                        <p className="text-sm text-fg-2">
                            This browser doesn't support passkeys. Try a recent version of Chrome,
                            Safari, or Edge.
                        </p>
                    </div>
                ) : passkeysLoading ? (
                    <div className="flex items-center gap-2 text-sm text-fg-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                ) : (
                    <div className="space-y-3 max-w-lg">
                        {passkeys.length === 0 ? (
                            <div className="bg-raised border border-line rounded-[var(--fds-radius)] p-4">
                                <p className="text-sm text-fg-2">
                                    No passkeys yet. Add one and you won't need your password on this device again.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {passkeys.map((pk) => (
                                    <div key={pk.id}
                                        className="flex items-center justify-between p-3 bg-raised border border-line rounded-[var(--fds-radius)]">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                                                <Fingerprint className="w-4 h-4 text-accent" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-fg truncate">
                                                    {pk.friendly_name || 'Passkey'}
                                                </p>
                                                {pk.created_at && (
                                                    <p className="text-xs text-fg-2">
                                                        Added {new Date(pk.created_at).toLocaleDateString('en-GB', {
                                                            day: 'numeric', month: 'short', year: 'numeric'
                                                        })}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDeletePasskey(pk.id)}
                                            className="p-1.5 rounded hover:bg-danger/10 text-fg-2 hover:text-danger transition-colors shrink-0"
                                            title="Remove this passkey"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={handleRegisterPasskey}
                            disabled={registeringPasskey}
                            className="flex items-center justify-center gap-2 bg-panel border border-line hover:bg-raised text-fg px-5 py-2.5 rounded-[var(--fds-radius)] font-medium transition-all disabled:opacity-50"
                        >
                            {registeringPasskey
                                ? <><Loader2 className="w-4 h-4 animate-spin" /> Waiting for your device…</>
                                : <><Plus className="w-4 h-4" /> Add a passkey</>}
                        </button>

                        {passkeys.length > 0 && (
                            <p className="text-xs text-fg-2/70">
                                Keep your password as a backup — removing every passkey would otherwise
                                leave you locked out on a device that can't use one.
                            </p>
                        )}
                    </div>
                )}
            </div>

            <div className="border-t border-line pt-8 mt-8">
                <h4 className="font-bold text-fg flex items-center gap-2 mb-4">
                    <Mail className="w-4 h-4" /> Email Address
                </h4>
                <div className="bg-raised border border-line rounded-[var(--fds-radius)] p-4">
                    <span className="text-sm text-fg-2">
                        To change your email address, please contact our support team at{' '}
                        <a
                            href="mailto:support@forgedash.uk"
                            className="text-accent hover:underline font-medium"
                        >
                            support@forgedash.uk
                        </a>
                        .
                    </span>
                </div>
            </div>
        </div>
    );
};
